
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getAssetPath } from "@/utils/assetUtils";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { fetchUserProfile } from "@/utils/apiUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileHeader from "./MobileHeader";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

const DashboardLayout = ({ children }: DashboardLayoutProps) => {
  const [logoLoaded, setLogoLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  
  // Use React Query to fetch user data
  const { data: userData } = useQuery({
    queryKey: ["userProfile"],
    queryFn: fetchUserProfile,
    staleTime: 300000, // 5 minutes
    retry: 2,
  });
  
  const userInitial = userData?.first_name 
    ? userData.first_name.charAt(0).toUpperCase()
    : userData?.username
      ? userData.username.charAt(0).toUpperCase()
      : "D";
  
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      toast({
        title: "Session expired",
        description: "Please login again to continue",
        variant: "destructive",
      });
      navigate("/login");
    }
  }, [navigate, toast]);
  
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userData");
    navigate("/login");
  };
  
  const isAccountPage = location.pathname === "/account";
  
  return (
    <div className="min-h-screen flex flex-col bg-white">
      {isMobile ? (
        <MobileHeader userInitial={userInitial} />
      ) : (
        <header className="bg-black border-b border-gray-700 py-4 px-6 shadow-sm">
          <div className="container mx-auto flex justify-between items-center">
            <Link to="/dashboard" className="flex items-center">
              <div className="px-6 py-2 rounded-lg bg-black">
                {!logoLoaded && <h1 className="text-white text-2xl font-bold">DISCO</h1>}
                <img
                  src={getAssetPath("disco_logo.png")}
                  alt="Disco Logo"
                  className={`h-8 ${logoLoaded ? 'block' : 'hidden'}`}
                  onLoad={() => setLogoLoaded(true)}
                  onError={() => console.error("Failed to load logo")}
                />
              </div>
            </Link>
            
            <div className="flex items-center gap-6">
              <Link 
                to="/operator-trip-list" 
                className="px-4 py-2 text-sm font-semibold text-black rounded-lg gradient-bg hover:opacity-90 transition-opacity"
              >
                Manage My Claimed Trips
              </Link>
              
              <Link 
                to="/driver-trip-sync" 
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-black hover:bg-gray-800 transition-colors"
              >
                Modify A Specific Trip
              </Link>
              
              <Link 
                to="/modify-trip" 
                className="px-4 py-2 text-sm font-semibold text-black rounded-lg gradient-bg hover:opacity-90 transition-opacity hidden"
              >
                Modify Trip
              </Link>
              
              <Link to="/dashboard" className={`text-white hover:text-neon-green transition-colors ${!isAccountPage ? 'font-medium' : ''}`}>
                Dashboard
              </Link>
              
              <Link to="/account" className="flex items-center gap-2">
                <span className={`text-white hover:text-neon-green transition-colors ${isAccountPage ? 'text-neon-green' : ''}`}>
                  My Account
                </span>
                <div className="h-10 w-10 rounded-full bg-neon-green text-black flex items-center justify-center font-medium cursor-pointer">
                  {userInitial}
                </div>
              </Link>
            </div>
          </div>
        </header>
      )}
      
      <main className="flex-grow py-6 bg-white">
        {children}
      </main>
      
      <footer className="bg-white border-t border-gray-200 text-black py-3 px-6 text-sm">
        <div className="container mx-auto flex flex-col md:flex-row justify-between items-center">
          <div>©2025 Disco. All Rights Reserved</div>
          <div className="flex gap-4 mt-2 md:mt-0">
            <a 
              href="https://www.discoveryconciergeservices.com/terms-conditions" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline text-black"
            >
              Terms & Conditions
            </a>
            <span>|</span>
            <a 
              href="https://www.discoveryconciergeservices.com/privacy-policy" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:underline text-black"
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DashboardLayout;
